import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchProfile, saveProfile } from '../services/profileService';
import { createBillingPortalSession, cancelTrialSubscription, fetchSubscriptionInfo } from '../services/stripeService';
import {
  formatSubscriptionStatus,
  canManageBillingInPortal,
  isTrialing,
  isTrialCancellationScheduled,
} from '../lib/subscription';
import {
  getPushConfig,
  hasActivePushSubscription,
  subscribeDevicePush,
  unsubscribeDevicePush,
  waitForServiceWorkerRegistration,
} from '../lib/pushNotifications';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import './Settings.css';

const Settings: React.FC = () => {
  const { user, session } = useAuth();
  const accessToken = session?.access_token;
  const [searchParams, setSearchParams] = useSearchParams();
  const [bettingName, setBettingName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState<number | null>(null);
  const [stakePerPoint, setStakePerPoint] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [deviceAlerts, setDeviceAlerts] = useState(false);
  const [pushAvailable, setPushAvailable] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] = useState<string | null>(null);
  const [subscriptionCancelAtPeriodEnd, setSubscriptionCancelAtPeriodEnd] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [cancelTrialLoading, setCancelTrialLoading] = useState(false);
  const [showCancelTrialConfirm, setShowCancelTrialConfirm] = useState(false);
  const [stripeSuccess, setStripeSuccess] = useState(false);

  // Avatar images (10 avatars in 5x2 grid)
  const avatars = Array.from({ length: 10 }, (_, i) => i + 1);

  // Load profile data on mount
  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const profile = await fetchProfile(user.id);

        if (profile) {
          setSubscriptionStatus(profile.subscription_status ?? null);
          setSubscriptionPeriodEnd(profile.subscription_current_period_end ?? null);
          setSubscriptionCancelAtPeriodEnd(profile.subscription_cancel_at_period_end === true);
          setBettingName(profile.display_name || '');
          // Convert avatar filename to number (e.g., "1.png" -> 1)
          if (profile.avatar) {
            const avatarNum = parseInt(profile.avatar.replace('.png', ''), 10);
            if (!isNaN(avatarNum)) {
              setSelectedAvatar(avatarNum);
            }
          }
          setStakePerPoint(profile.default_stake?.toString() || '');
          setCommissionRate(profile.commission_rate?.toString() || '');
          setCurrency(profile.currency || 'GBP');
          setEmailNotifications(profile.notifications_enabled ?? true);
          setDeviceAlerts(profile.device_alerts_enabled ?? false);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile settings. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const refreshSubscription = async () => {
      if (!session?.access_token) return;
      try {
        const info = await fetchSubscriptionInfo(session.access_token);
        if (cancelled) return;
        setSubscriptionStatus(info.subscription_status);
        setSubscriptionPeriodEnd(info.subscription_current_period_end);
        setSubscriptionCancelAtPeriodEnd(info.subscription_cancel_at_period_end);
      } catch (err) {
        console.warn('Settings: subscription info refresh failed', err);
      }
    };

    void refreshSubscription();
    return () => {
      cancelled = true;
    };
  }, [session?.access_token]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const config = await getPushConfig();
      if (cancelled) return;
      setPushAvailable(config.enabled);
      if (config.enabled) {
        const active = await hasActivePushSubscription();
        if (!cancelled && active) {
          setDeviceAlerts(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDeviceAlertsToggle = async (enabled: boolean) => {
    if (!user?.id || !accessToken) {
      setError('You must be logged in to change alert settings.');
      return;
    }
    setPushBusy(true);
    setPushMessage(null);
    setError(null);
    try {
      if (enabled) {
        const config = await getPushConfig();
        if (!config.enabled) {
          setPushMessage(
            'Device alerts are not available yet. Ensure VAPID keys and Redis are configured on the server.'
          );
          return;
        }
        const reg = await waitForServiceWorkerRegistration();
        if (!reg) {
          setPushMessage(
            'Install or open the production app to enable background alerts (service worker required).'
          );
          return;
        }
        const ok = await subscribeDevicePush(accessToken, reg);
        if (!ok) {
          setPushMessage('Could not enable alerts. Check notification permission and try again.');
          return;
        }
        setDeviceAlerts(true);
        await saveProfile(user.id, { device_alerts_enabled: true });
        setPushMessage('Device alerts enabled.');
      } else {
        await unsubscribeDevicePush(accessToken);
        setDeviceAlerts(false);
        await saveProfile(user.id, { device_alerts_enabled: false });
        setPushMessage('Device alerts disabled.');
      }
    } catch (err) {
      console.error('Device alerts toggle failed', err);
      setPushMessage('Failed to update device alerts. Please try again.');
    } finally {
      setPushBusy(false);
    }
  };

  const stripeReturnHandled = useRef(false);

  useEffect(() => {
    if (stripeReturnHandled.current || !user?.id) return;
    if (searchParams.get('stripe') !== 'success') return;
    stripeReturnHandled.current = true;
    setStripeSuccess(true);
    const next = new URLSearchParams(searchParams);
    next.delete('stripe');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
    fetchProfile(user.id).then((p) => {
      if (p) {
        setSubscriptionStatus(p.subscription_status ?? null);
        setSubscriptionPeriodEnd(p.subscription_current_period_end ?? null);
        setSubscriptionCancelAtPeriodEnd(p.subscription_cancel_at_period_end === true);
      }
    });
  }, [user?.id, searchParams, setSearchParams]);

  const handleSave = async () => {
    if (!user?.id) {
      setError('You must be logged in to save settings.');
      return;
    }

    // Validation
    if (stakePerPoint && parseFloat(stakePerPoint) < 1.00) {
      setError('Stake per Point must be at least 1.00');
      return;
    }

    if (commissionRate) {
      const rate = parseFloat(commissionRate);
      if (rate < 1.00 || rate > 100.00) {
        setError('Betfair Commission Rate must be between 1.00 and 100.00');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Convert avatar number to filename
      const avatarFilename = selectedAvatar ? `${selectedAvatar}.png` : null;

      // Convert string values to numbers (handle empty strings)
      const defaultStake = stakePerPoint ? parseFloat(stakePerPoint) : null;
      const commissionRateNum = commissionRate ? parseFloat(commissionRate) : null;

      await saveProfile(user.id, {
        display_name: bettingName || null,
        avatar: avatarFilename,
        default_stake: defaultStake,
        commission_rate: commissionRateNum,
        currency: currency,
        notifications_enabled: emailNotifications,
      });

      setSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    if (!session?.access_token) {
      setError('You must be signed in to manage billing.');
      return;
    }
    setBillingLoading(true);
    setError(null);
    try {
      const url = await createBillingPortalSession(session.access_token);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open billing portal');
      setBillingLoading(false);
    }
  };

  const handleCancelTrialClick = () => {
    if (!session?.access_token) {
      setError('You must be signed in to cancel your trial.');
      return;
    }
    setShowCancelTrialConfirm(true);
  };

  const handleCancelTrialConfirm = async () => {
    if (!session?.access_token) return;

    setCancelTrialLoading(true);
    setError(null);
    try {
      const result = await cancelTrialSubscription(session.access_token);
      setSubscriptionCancelAtPeriodEnd(result.subscription_cancel_at_period_end);
      if (result.subscription_current_period_end) {
        setSubscriptionPeriodEnd(result.subscription_current_period_end);
      }
      setShowCancelTrialConfirm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel trial');
    } finally {
      setCancelTrialLoading(false);
    }
  };

  const trialCancellationScheduled = isTrialCancellationScheduled(
    subscriptionStatus,
    subscriptionCancelAtPeriodEnd
  );

  const statusBadgeClass = (() => {
    if (!subscriptionStatus) return 'status-expired';
    if (subscriptionStatus === 'active') return 'status-active';
    if (subscriptionStatus === 'trialing') return 'status-trial';
    return 'status-expired';
  })();

  const periodLabel = trialCancellationScheduled
    ? 'Trial access until'
    : subscriptionStatus === 'trialing'
      ? 'Trial ends'
      : 'Current period ends';

  const formattedPeriodEnd = subscriptionPeriodEnd
    ? new Date(subscriptionPeriodEnd).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

  const cancelTrialEndLabel = subscriptionPeriodEnd
    ? formattedPeriodEnd
    : 'the end of your trial';

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-content">
        <div className="settings-header-row">
          <h1>Settings</h1>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="settings-button settings-button-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {success && (
          <div className="settings-alert settings-success">
            <strong>Success!</strong> Your settings have been saved.
          </div>
        )}

        {error && (
          <div className="settings-alert settings-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {stripeSuccess && (
          <div className="settings-alert settings-success">
            <strong>Payment received.</strong> Your subscription will update in a few seconds if it does not show as active yet.
          </div>
        )}

        {/* Profile Section */}
        <section className="settings-section">
          <h2 className="settings-section-title">Profile</h2>
          <div className="settings-section-content">
            <div className="settings-field">
              <label htmlFor="betting-name">Display Name</label>
              <input
                type="text"
                id="betting-name"
                value={bettingName}
                onChange={(e) => setBettingName(e.target.value)}
                placeholder="Enter your display name"
                className="settings-input"
              />
            </div>

            <div className="settings-field">
              <label>Avatar</label>
              <div className="avatar-grid">
                {avatars.map((avatarNum) => (
                  <div
                    key={avatarNum}
                    className={`avatar-option ${selectedAvatar === avatarNum ? 'selected' : ''}`}
                    onClick={() => setSelectedAvatar(avatarNum)}
                  >
                    <img
                      src={`/avatars/${avatarNum}.png`}
                      alt={`Avatar ${avatarNum}`}
                      className="avatar-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Betting Settings Section */}
        <section className="settings-section">
          <h2 className="settings-section-title">Betting Settings</h2>
          <div className="settings-section-content">
            <div className="betting-settings-row">
              <div className="settings-field">
                <label htmlFor="stake-per-point">Stake per Point</label>
                <input
                  type="number"
                  id="stake-per-point"
                  value={stakePerPoint}
                  onChange={(e) => setStakePerPoint(e.target.value)}
                  placeholder="1.00"
                  step="0.01"
                  min="1.00"
                  className="settings-input settings-input-number"
                />
                {stakePerPoint && parseFloat(stakePerPoint) < 1.00 && (
                  <span className="settings-validation-error">Minimum value is 1.00</span>
                )}
              </div>

              <div className="settings-field">
                <label htmlFor="commission-rate">Betfair Commission Rate (%)</label>
                <input
                  type="number"
                  id="commission-rate"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  placeholder="1.00"
                  step="0.01"
                  min="1.00"
                  max="100.00"
                  className="settings-input settings-input-number"
                />
                {commissionRate && (parseFloat(commissionRate) < 1.00 || parseFloat(commissionRate) > 100.00) && (
                  <span className="settings-validation-error">Value must be between 1.00 and 100.00</span>
                )}
              </div>

              <div className="settings-field">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="settings-select"
                >
                  <option value="GBP">GBP - British Pound</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Subscription Information Section */}
        <section className="settings-section">
          <h2 className="settings-section-title">Subscription Information</h2>
          <div className="settings-section-content">
            <div className="settings-field">
              <label>Status</label>
              <div className="subscription-status">
                <span className={`status-badge ${statusBadgeClass}`}>
                  {trialCancellationScheduled
                    ? 'Trial — cancels at period end'
                    : formatSubscriptionStatus(subscriptionStatus)}
                </span>
              </div>
            </div>

            <div className="settings-field">
              <label>{periodLabel}</label>
              <div className="settings-readonly">{formattedPeriodEnd}</div>
            </div>

            {trialCancellationScheduled && (
              <div className="settings-alert settings-success subscription-cancel-notice">
                Your trial is cancelled and will not renew. You keep access until {formattedPeriodEnd}{' '}
                and will not be charged.
              </div>
            )}

            <div className="settings-field settings-subscription-actions">
              {canManageBillingInPortal(subscriptionStatus) ? (
                <button
                  type="button"
                  className="settings-button settings-button-secondary"
                  onClick={handleManageBilling}
                  disabled={billingLoading}
                >
                  {billingLoading ? 'Opening…' : 'Manage Billing'}
                </button>
              ) : isTrialing(subscriptionStatus) ? (
                <>
                  {!trialCancellationScheduled && (
                    <button
                      type="button"
                      className="settings-button settings-button-secondary settings-button-danger-outline"
                      onClick={handleCancelTrialClick}
                      disabled={cancelTrialLoading}
                    >
                      {cancelTrialLoading ? 'Cancelling…' : 'Cancel trial'}
                    </button>
                  )}
                  {!trialCancellationScheduled && (
                    <Link
                      to="/subscribe"
                      className="settings-button settings-button-secondary"
                      style={{ textAlign: 'center', textDecoration: 'none', display: 'inline-block' }}
                    >
                      Subscribe early
                    </Link>
                  )}
                </>
              ) : (
                <Link
                  to="/subscribe"
                  className="settings-button settings-button-secondary"
                  style={{ textAlign: 'center', textDecoration: 'none', display: 'inline-block' }}
                >
                  Subscribe
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Daily digest email (only); welcome email is sent to all new accounts separately */}
        <section className="settings-section">
          <h2 className="settings-section-title">Notifications</h2>
          <div className="settings-section-content">
            <div className="settings-field settings-field-toggle">
              <div className="toggle-container">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    className="toggle-input"
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Daily Race Card Email</span>
                </label>
                <p className="toggle-subtext">
                  One email each morning with the UK card snapshot for that day (or a short note when there are no qualifying picks).
                </p>
              </div>
            </div>

            <div className="settings-field settings-field-toggle">
              <div className="toggle-container">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={deviceAlerts}
                    disabled={pushBusy || !pushAvailable}
                    onChange={(e) => void handleDeviceAlertsToggle(e.target.checked)}
                    className="toggle-input"
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Push Notifications</span>
                </label>
                <p className="toggle-subtext">
                  Receive a notification when your bet wins.
                </p>
                {!pushAvailable && (
                  <p className="toggle-subtext" style={{ marginTop: '0.5rem', opacity: 0.85 }}>
                    Background alerts are not configured on the server yet.
                  </p>
                )}
                {pushMessage && (
                  <p className="toggle-subtext" style={{ marginTop: '0.5rem' }}>
                    {pushMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Save Changes Button */}
        <div className="settings-actions">
          {success && (
            <div className="settings-success-inline">
              <strong>Success!</strong> Your settings have been saved.
            </div>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="settings-button settings-button-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={showCancelTrialConfirm}
        title="Cancel your free trial?"
        message={
          <>
            You will keep access until <strong>{cancelTrialEndLabel}</strong> and will not be charged.
          </>
        }
        confirmLabel="Cancel trial"
        cancelLabel="Keep trial"
        confirmVariant="danger"
        loading={cancelTrialLoading}
        onConfirm={() => void handleCancelTrialConfirm()}
        onCancel={() => {
          if (!cancelTrialLoading) setShowCancelTrialConfirm(false);
        }}
      />
    </div>
  );
};

export default Settings;

